import { definePreset } from '@primevue/themes'
import Aura from '@primevue/themes/aura'

const SlicealPreset = definePreset(Aura, {
  semantic: {
    primary: {
      25: '#EEEFFF',
      50: '#EEEFFF',
      100: '#DEE2FF',
      200: '#C3C8FF',
      300: '#9FA4FF',
      400: '#7E79FF',
      500: '#6755FB',
      600: '#5C3CF0',
      700: '#502ED4',
      800: '#4228AC',
      900: '#23184E',
      950: '#23184E',
    },

    success: {
      50: '#EEFDF7',
      100: '#D8F9EB',
      200: '#B6F2DA',
      300: '#86E6C2',
      400: '#57D8A8',
      500: '#2FC48D',
      600: '#1EA775',
      700: '#17835C',
      800: '#146A4B',
      900: '#12563D',
    },

    warning: {
      50: '#FFF9EB',
      100: '#FFF0C9',
      200: '#FFE29A',
      300: '#FFD166',
      400: '#FFBD33',
      500: '#F7A600',
      600: '#D98D00',
      700: '#B97500',
      800: '#9B6200',
      900: '#7F5100',
    },

    danger: {
      50: '#FFF3F3',
      100: '#FFE2E2',
      200: '#FFC8C8',
      300: '#FFA1A1',
      400: '#FF7070',
      500: '#F04444',
      600: '#D92D2D',
      700: '#B42323',
      800: '#8F1C1C',
      900: '#761818',
    },

    info: {
      50: '#EEF5FF',
      100: '#D9E9FF',
      200: '#BCD8FF',
      300: '#91C0FF',
      400: '#63A5FF',
      500: '#3D89F5',
      600: '#2C72DB',
      700: '#225CC0',
      800: '#1E4A9A',
      900: '#1D3F7F',
    },
    colorScheme: {
      light: {
        text: {
          color: '#0A0A0A', // overrides --p-text-color
          mutedColor: '#414651', // overrides --p-text-muted-color
        },
      },
      dark: {
        text: {
          color: '#F9FAFB',
          mutedColor: '#D1D5DB',
        },
        surface: {
          0: '#0E1014', // page background
          50: '#15181D', // subtle elevated bg
          100: '#1B1F26', // card / chip bg
          200: '#262B33', // borders subtle / raised
          300: '#333944',
          400: '#525964',
          500: '#71717A',
          600: '#A1A1AA',
          700: '#D4D4D8',
          800: '#E4E4E7',
          900: '#F4F4F5',
          950: '#FAFAFA',
        },
        primary: {
          25: '#1A1640',
          50: '#1F1A4A',
          100: '#2A2363',
          200: '#3A2F8A',
        },
        success: {
          50: '#0F2A1E',
          100: '#143A29',
          200: '#1A5239',
        },
        warning: {
          50: '#3A2A0A',
          100: '#4D3A0F',
          200: '#6B521A',
        },
        danger: {
          50: '#3A1414',
          100: '#4D1A1A',
          200: '#6B2424',
        },
        info: {
          50: '#0F1F3A',
          100: '#152A52',
          200: '#1D3C7A',
        },
        content: {
          borderColor: '#262B33',
        },
      },
    },
  },

  primitive: {
    borderRadius: {
      sm: '6px',
      md: '10px',
      lg: '14px',
      xl: '20px',
    },
    slicealColors: {
      lavender: '#EBE9FF',
      primaryDeep: '#24195F',
      primaryGlow: '#7A6BFF',
    },
    fontFamily:
      '"SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    // Display face for hero headings (`Text type="display-xl"`). Falls back to
    // the body stack, so headings stay correct if the DM Sans files are absent.
    headingFontFamily:
      '"DM Sans", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  extend: {
    sliceal: {
      landingNavbar: '{slicealColors.lavender}',
      signUpRight: '{slicealColors.lavender}',
      // Token-wallet lime palette (XRPL token balance, token_received rows) —
      // same in both schemes → --p-sliceal-token-bg-light, -bg-mid, -border,
      // -border-bright, -text, -deep
      token: {
        bgLight: '#F5FEE7',
        bgMid: '#DCFCE7',
        border: '#A3E635',
        borderBright: '#A9F250',
        text: '#58A40C',
        deep: '#447D0E',
      },
    },
    // App canvas + table header greys (neutral, not slate-tinted like the
    // surface ramp) → --p-sliceal-page-background / --p-sliceal-table-header;
    // Figma "Gray" ramp + settings icon tile → --p-sliceal-gray-*, --p-sliceal-icon-tile
    colorScheme: {
      light: {
        sliceal: {
          pageBackground: '#FAFAFA', // --p-sliceal-page-background
          tableHeader: '#F6F6F6', // --p-sliceal-table-header
          gray: {
            100: '#F5F5F5',
            200: '#E9EAEB',
            400: '#A4A7AE',
            600: '#535862',
            900: '#181D27',
          },
          iconTile: '#F5FAFF',
          // Invested dashboard hero wash (Figma 33753:118287) → --p-sliceal-hero-wash
          heroWash: '#EDEBF5',
        },
      },
      dark: {
        sliceal: {
          pageBackground: '#0E1014',
          tableHeader: '#15181D',
          gray: {
            100: '#1B1F26',
            200: '#262B33',
            400: '#A1A1AA',
            600: '#D1D5DB',
            900: '#F9FAFB',
          },
          iconTile: '#1B1F26',
          heroWash: '#1E1B2E',
        },
      },
    },
  },
})

export default SlicealPreset
